/**
 * V3 dynamic listing: color swatch interactions for the V3 product card.
 *
 * Hovering (desktop) or tapping (touch) a swatch swaps the card image to the
 * variant image, points the card links at the variant URL and refreshes the
 * plain regular/compare-at price. A swatch click never navigates by itself. Promo-code prices and discount messages
 * (rules 1 and 2 in snippets/price.liquid) are left untouched because they are
 * product-level, not variant-level.
 */
(function () {
  if (customElements.get('v3-dynamic-listing')) return;

  const formatMoney = (cents) => {
    if (window.FoxTheme && FoxTheme.Currency && typeof FoxTheme.Currency.formatMoney === 'function') {
      return FoxTheme.Currency.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  };

  class V3DynamicListing extends HTMLElement {
    connectedCallback() {
      this.isTouch = window.matchMedia('(hover: none)').matches;
      this.onSwatchEnter = this.onSwatchEnter.bind(this);
      this.onSwatchClick = this.onSwatchClick.bind(this);

      this.querySelectorAll('[data-v3pc-swatch]').forEach((swatch) => {
        swatch.addEventListener('mouseenter', this.onSwatchEnter);
        swatch.addEventListener('focus', this.onSwatchEnter);
        swatch.addEventListener('click', this.onSwatchClick);
      });

      // Tag filter tiles (optional "Filter tile" blocks)
      this.filterButtons = Array.from(this.querySelectorAll('[data-v3dl-filter]'));
      this.filterButtons.forEach((btn) => {
        btn.addEventListener('click', () => this.toggleFilter(btn));
      });
      this.clearButton = this.querySelector('[data-v3dl-clear]');
      if (this.clearButton) {
        this.clearButton.addEventListener('click', () => this.clearFilter());
      }
    }

    clearFilter() {
      this.filterButtons.forEach((b) => {
        b.classList.remove('is-active');
        b.setAttribute('aria-pressed', 'false');
      });
      this.applyFilter('');
    }

    toggleFilter(btn) {
      const wasActive = btn.classList.contains('is-active');
      this.filterButtons.forEach((b) => {
        b.classList.remove('is-active');
        b.setAttribute('aria-pressed', 'false');
      });
      const tag = wasActive ? '' : (btn.dataset.v3dlFilter || '').trim().toLowerCase();
      if (tag) {
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
      }
      this.applyFilter(tag);
    }

    applyFilter(tag) {
      const needle = tag ? '|' + tag + '|' : '';
      let visible = 0;
      this.querySelectorAll('[data-v3dl-tags]').forEach((cell) => {
        const show = !needle || (cell.dataset.v3dlTags || '').toLowerCase().indexOf(needle) !== -1;
        cell.hidden = !show;
        if (show) visible += 1;
      });
      // Promo banners only make sense in the unfiltered grid.
      this.querySelectorAll('.v3dl__cell--banner').forEach((cell) => {
        cell.hidden = !!needle;
      });
      const empty = this.querySelector('[data-v3dl-filter-empty]');
      if (empty) empty.hidden = !(needle && visible === 0);
      const grid = this.querySelector('.v3dl__grid');
      if (grid) grid.hidden = !!(needle && visible === 0);
      // Header: bordered box + "Clear filter" button while a filter is active
      this.classList.toggle('v3dl--filtered', !!needle);
      if (this.clearButton) this.clearButton.hidden = !needle;
    }

    onSwatchEnter(event) {
      if (this.isTouch) return;
      this.applySwatch(event.currentTarget);
    }

    onSwatchClick(event) {
      // Clicking a swatch only previews the variant (image, price, links);
      // it never navigates. The card image, title and button already point
      // at the selected variant's URL after applySwatch().
      event.preventDefault();
      this.applySwatch(event.currentTarget);
    }

    applySwatch(swatch) {
      const card = swatch.closest('[data-v3pc]');
      if (!card) return;

      card.querySelectorAll('[data-v3pc-swatch]').forEach((el) => el.classList.remove('is-active'));
      swatch.classList.add('is-active');

      // Floating option name (bottom-left of the image)
      const label = card.querySelector('[data-v3pc-variant-label]');
      if (label && swatch.dataset.value) {
        label.textContent = swatch.dataset.value;
        label.hidden = false;
      }

      const img = card.querySelector('[data-v3pc-img]');
      if (swatch.dataset.image) card.classList.add('is-swatched');
      if (img && swatch.dataset.image && img.dataset.currentSrc !== swatch.dataset.image) {
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = swatch.dataset.image;
        img.dataset.currentSrc = swatch.dataset.image;
      }

      if (swatch.dataset.url) {
        card.querySelectorAll('[data-v3pc-link]').forEach((link) => {
          link.setAttribute('href', swatch.dataset.url);
        });
      }

      this.updatePrice(card, swatch);
    }

    updatePrice(card, swatch) {
      const priceWrap = card.querySelector('[data-v3pc-price]');
      if (!priceWrap) return;
      const priceEl = priceWrap.querySelector('.f-price');
      if (!priceEl) return;
      // Product-level promo code (rule 1): variant prices don't apply.
      if (priceEl.querySelector('.f-price-item--promo-card')) return;

      // Discount message (rule 2): generated from pdp_discount_percent /
      // pdp_discount_amount in price.liquid. Product-level text stays as is;
      // a swatch carrying its own variant-level values (data-discount-message /
      // percent / amount) swaps the text and drives the computed price.
      const messageEl = priceEl.querySelector('.discount-message-price');
      if (messageEl) {
        if (swatch.dataset.discountMessage) messageEl.innerHTML = swatch.dataset.discountMessage;
        const struck = priceEl.querySelector('.f-price__regular .f-price-item--regular');
        const price = parseInt(swatch.dataset.price || '', 10);
        if (struck && !Number.isNaN(price)) struck.innerHTML = formatMoney(price);
        // Percent first, otherwise the fixed amount (cents); swatch values win over .f-price.
        const discounted = priceEl.querySelector('[data-msrp-final-price]');
        const hasVariantDiscount = swatch.dataset.discountPercent !== undefined || swatch.dataset.discountAmount !== undefined;
        const percent = parseFloat((hasVariantDiscount ? swatch.dataset.discountPercent : priceEl.dataset.msrpDiscountPercent) || '');
        const amount = parseInt((hasVariantDiscount ? swatch.dataset.discountAmount : priceEl.dataset.msrpDiscountAmount) || '', 10);
        if (discounted && !Number.isNaN(price)) {
          if (!Number.isNaN(percent)) {
            discounted.innerHTML = formatMoney(Math.round((price * (100 - percent)) / 100));
          } else if (!Number.isNaN(amount)) {
            discounted.innerHTML = formatMoney(Math.max(0, price - amount));
          }
        }
        return;
      }

      const price = parseInt(swatch.dataset.price || '', 10);
      const compare = parseInt(swatch.dataset.compare || '', 10);
      if (Number.isNaN(price)) return;

      const onSale = !Number.isNaN(compare) && compare > price;
      priceEl.classList.toggle('f-price--on-sale', onSale);
      priceEl.classList.toggle('f-price--sold-out', swatch.dataset.available === 'false');

      const regular = priceEl.querySelector('.f-price__regular .f-price-item--regular');
      if (regular) regular.innerHTML = formatMoney(price);
      const sale = priceEl.querySelector('.f-price__sale .f-price-item--sale');
      if (sale) sale.innerHTML = formatMoney(price);
      const struck = priceEl.querySelector('.f-price__sale .f-price-item--regular s');
      if (struck && onSale) struck.innerHTML = formatMoney(compare);

    }
  }

  customElements.define('v3-dynamic-listing', V3DynamicListing);
})();
